import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { useParams, useHistory } from 'react-router-dom';
import { useMutation } from 'react-query';
import ProvisioningStyles from 'pages/cloud-onboarding/style';
import { getAccessToken } from 'utils';
import { orderId, orderPayload, toggleSspModels, isValidOwners, uploadedFilesState, showPreviewOrder } from 'pages/provisioning/aws/account-vending/store';
import {
    formOrderObjFromOrderDetails,
    resetToggleSspModal,
    resetOrder,
    formUploadObjFromOrderDetails,
    formatAwsAccountVendingPreview,
} from 'pages/provisioning/aws/account-vending/utils';
import AwsPrivilegedADGroupModal from 'pages/provisioning/aws/account-vending/components/Form/AccessManagementSection/AwsPrivilegedADGroupModal';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/account-vending/ProvisioningHeader'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/account-vending/SspForm'));
const OrderSuccessDialog = React.lazy(() => import('pages/provisioning/aws/account-vending/OrderSuccessDialog'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/account-vending/ShowCostDetails'));
const PreviewOrderDetails = React.lazy(() => import('pages/provisioning/aws/account-vending/components/PreviewOrderDetails'));

const AwsAccountVendingProvisioning = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [order, setOrder] = useRecoilState(orderPayload);
    const [, setUploadedFiles] = useRecoilState(uploadedFilesState);
    const resetUploadFiles = useResetRecoilState(uploadedFilesState);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, resetValidOwners] = useRecoilState(isValidOwners);
    const [showPreview, setShowPreview] = useRecoilState(showPreviewOrder);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const history = useHistory();
    const gpid = getAccessToken('claims')?.gpid;
    const axios = useAxios();

    const bAppPostApi = useMutation((payload) => axios.post(endPoints.riServices.COMMON.businessCiDetails, payload));

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    // Helper function to fetch landing zone details
    const fetchLandingZoneDetails = async (orderData) => {
        if (orderData?.appProfile && orderData?.awsSector && orderData?.department && orderData?.appRegion && orderData?.awsEnvironment) {
            const payload = {
                applicationServiceName: orderData.appProfile,
                sector: orderData.awsSector,
                environment: orderData.awsEnvironment,
                department: orderData.department,
                applicationRegion: orderData.appRegion,
            };
            try {
                const landingZoneResponse = await axios.post(endPoints.aws.lookups.acctVendingLandingZone, payload);
                return landingZoneResponse?.data;
            } catch (error) {
                // Landing zone API error
                return null;
            }
        }
        return null;
    };

    async function checkCatalog() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}`);
        const costs = await getCosts();
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetailsV2}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails);
            const uploadObj = formUploadObjFromOrderDetails(catalogServiceId, orderDetails);
            const landingZoneDetails = await fetchLandingZoneDetails(orderObj);
            let bAppResponse = null;
            if (orderObj?.businessApp && ((orderObj?.accountType === 'yes' && orderObj?.hasApplicationService === 'yes') || orderObj?.accountType === 'no')) {
                bAppResponse = await bAppPostApi?.mutateAsync({
                    businessApplication: orderObj?.businessApp,
                    applicationService: null,
                });
            }
            setUploadedFiles(uploadObj);

            if (operation === 'preview') {
                // Handle validation report view - use order details instead of preview API
                const catalogConfig = orderDetails?.catalogServices?.[0]?.catalogConfig || {};
                const formattedDetails = formatAwsAccountVendingPreview(catalogConfig, 'orderDetails');
                setShowPreview('preview-order');
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                    businessAppDetails: bAppResponse?.data?.applications?.[0],
                    landingZoneDetails,
                    emptyCost: costs,
                    environmentDetails: catalogConfig,
                    validationDetails: orderDetails?.validationDetails,
                    orderId: cartItemId,
                    currentOrderStatus: orderDetails?.currentStatus?.status,
                    createdBy: orderDetails?.created?.by?.id,
                    previewOrder: formattedDetails,
                }));
            } else {
                // Handle normal edit view
                setOrder((previous) => ({
                    ...previous,
                    ...orderObj,
                    businessAppDetails: bAppResponse?.data?.applications?.[0],
                    landingZoneDetails,
                    emptyCost: costs,
                }));
            }
        } else {
            setOrder((previous) => ({
                ...previous,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    React.useEffect(() => {
        if (order?.createdBy && order?.createdBy !== gpid) {
            history.push('/orders');
        }
    }, [order?.createdBy, gpid, history]);

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
        resetValidOwners({
            isvalidAppOwner: true,
        });
        resetUploadFiles();
        setShowPreview(null);
    };

    React.useEffect(() => {
        if (catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [catalogCode]);

    React.useEffect(() => {
        return () => reset();
    }, []);
    return (
        <>
            <Helmet>
                <title>AWS Account Vending</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        {showPreview === 'preview-order' && order?.previewOrder && <PreviewOrderDetails />}
                        {showPreview === 'pre-preview-order' && order?.prePreviewOrder && <PreviewOrderDetails />}
                        {!showPreview && (
                            <Box sx={ProvisioningStyles.sspform_wrapper} data-testid="index-form" className="contentContainer">
                                <SspForm />
                            </Box>
                        )}
                        <br />
                        <OrderSuccessDialog />
                        <AwsPrivilegedADGroupModal />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default AwsAccountVendingProvisioning;
